import { useEffect, useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Badge,
  Box,
  ButtonBase,
  ClickAwayListener,
  Popper,
  Fade
} from '@mui/material';

// project import
import ToDoList from 'apps/todo-list';
import Transitions from 'components/@extended/Transitions';

// store
import useAuth from 'hooks/useAuth';
import useTasksStore from 'stores/tasksStore';

// icons
import { MdFormatListBulleted } from "react-icons/md";

const TaskButton = () => {
  const theme = useTheme();
  const anchorRef = useRef(null);
  
  const [open, setOpen] = useState(false);
  
  //Role validation
  const { user, ValidateRole } = useAuth();
  const authorizedRoles = [1, 100, 110];
  let validation = ValidateRole(user?.role, authorizedRoles);

  // Pending task count for the badge
  const tasks = useTasksStore(state => state.tasks);
  const pendingCount = tasks.filter(
    task => String(task.usuario_id) === String(user?.id) && task.estado !== 'completado'
  ).length;

  // Hydrate the store from the login payload so the badge already shows a count
  // before the popover (and therefore the ToDoList component) is ever opened.
  useEffect(() => {
    if (!user?.id) return;

    const initData = user?.initData?.todoList;
    if (Array.isArray(initData)) {
      useTasksStore.getState().initializeFromUserData(initData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  // If user doesn't have authorized role, don't render the component
  if (validation !== true) return null;

  return (
    <>
      <Box sx={{ flexShrink: 0, ml: 0.75 }}>
        <ButtonBase
          sx={{
            p: 0.25,
            borderRadius: 1,
            '&:hover': { 
              bgcolor: theme.palette.mode === 'dark' ? 'secondary.light' : 'secondary.lighter' 
            },
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.secondary.dark}`,
              outlineOffset: 2
            }
          }}
          aria-label="open tasks"
          ref={anchorRef}
          aria-controls={open ? 'tasks-popover' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <Badge badgeContent={pendingCount} color="success">
            <MdFormatListBulleted style={{ fontSize: '1.5rem', color: theme.palette.primary.light }} />
          </Badge>
        </ButtonBase>
      </Box>
      
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
        sx={{ zIndex: 1100 }}
      >
        {({ TransitionProps }) => (
          <Transitions type="fade" in={open} {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  {open && (
                    <ToDoList
                      usuarioId={user?.id}
                      maxRows={8}
                      onClose={() => setOpen(false)}
                    />
                  )}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  );
};

export default TaskButton;

// Styled Paper component with fade transition
const Paper = ({ children, sx, ...other }) => {
  return (
    <Fade in>
      <Box
        sx={{
          boxShadow: '0px 8px 10px rgba(0, 0, 0, 0.14), 0px 3px 14px rgba(0, 0, 0, 0.12)',
          minWidth: 285,
          maxWidth: 420,
          width: '100%',
          borderRadius: 1.5,
          ...sx
        }}
        {...other}
      >
        {children}
      </Box>
    </Fade>
  );
};
